import { ModalCustom } from "components/ModalCustom";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { formatarDinheiro } from "_utils/Money";
import { FaExclamationTriangle, FaExternalLinkAlt, FaFlag, FaSearch } from "react-icons/fa";
import { Popover, Tooltip } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { PrimaryButton } from "components/Button/style";
import { useState, useEffect } from "react";
import {
  AlertaAReceber,
  BuscaIcone,
  BuscaInput,
  BuscaWrapper,
  CampoResumo,
  CamposGroup,
  ContainerFechar,
  ContainerResumo,
  ContainerTable,
  CustosAdicionados,
  FlagUrgente,
  LabelResumo,
  LinhaInfo,
  LinkRecibo,
  ObservacaoLabel,
  ObservacaoResumo,
  ObservacaoTexto,
  UrgenteLinha,
  ValorResumo,
} from "./style";
import {
  IDetalheLoteGerenciarPagamento,
  IResumoLoteGerenciarPagamento,
} from "../../../../../interface/IDetalheLoteGerenciarPagamento";

const TIPO_LANCAMENTO_LABEL: Record<string, string> = {
  C: "Custos do processo",
  P: "Pagamento",
  D: "Débitos a receber",
};

interface IProps {
  show: boolean;
  dados: IResumoLoteGerenciarPagamento | null;
  handleClose: () => void;
  onClickProcesso: (param: string) => void;
  onBaixarRecibo: (idLote: number) => void;
}

function CustosLancadosColunaCell({ custos }: { custos: { descricao: string; valor: number }[] }) {
  const MAX_VISIVEIS = 1;
  const visiveis = custos.slice(0, MAX_VISIVEIS);
  const extras = custos.length - MAX_VISIVEIS;

  const popover = (
    <Popover id="popover-custos" style={{ minWidth: 260 }}>
      <Popover.Body style={{ padding: "8px" }}>
        <div style={{ border: "1px solid #dee2e6", borderRadius: "4px", overflow: "hidden" }}>
          {custos.map((c, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 14px",
                fontSize: "0.85rem",
                borderBottom: i < custos.length - 1 ? "1px solid #f0f0f0" : "none",
                backgroundColor: "#fff",
              }}
            >
              <span>{c.descricao}</span>
              <span style={{ marginLeft: 16, fontWeight: 600 }}>{formatarDinheiro(c.valor)}</span>
            </div>
          ))}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden", width: "100%" }}>
      {visiveis.map((c, i) => (
        <span key={i} style={{ fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto", minWidth: 0 }}>
          {c.descricao}
        </span>
      ))}
      {extras > 0 && (
        <OverlayTrigger placement="top" trigger={["hover", "focus"]} overlay={popover}>
          <span style={{ flexShrink: 0, background: "#f5f5f5", border: "1px solid #7f7f7f", borderRadius: "50px", color: "#7f7f7f", padding: "3px 8px", fontSize: "0.75rem", cursor: "pointer" }}>
            +{extras}
          </span>
        </OverlayTrigger>
      )}
    </div>
  );
}

export function ModalDetalhesLote({
  show,
  dados,
  handleClose,
  onClickProcesso,
  onBaixarRecibo,
}: IProps) {
  const [busca, setBusca] = useState("");
  const [processosFiltrados, setProcessosFiltrados] = useState<
    IDetalheLoteGerenciarPagamento[]
  >([]);

  useEffect(() => {
    if (!dados) return;
    if (!busca.trim()) {
      setProcessosFiltrados(dados.processos ?? []);
      return;
    }
    const termo = busca.toLowerCase().replace("-", "");
    setProcessosFiltrados(
      (dados.processos ?? []).filter(
        (p) =>
          String(p.idProcesso).includes(termo) ||
          (p.placa && p.placa.replace("-", "").toLowerCase().includes(termo)),
      ),
    );
  }, [busca, dados]);

  const columns: IColum<IDetalheLoteGerenciarPagamento>[] = [
    {
      name: "Processo",
      selector: ({ idProcesso, param }: IDetalheLoteGerenciarPagamento) => (
        <div
          className="link-primary"
          style={{ cursor: "pointer" }}
          onClick={() => onClickProcesso(param)}
        >
          {idProcesso}
        </div>
      ),
      width: "100px",
    },
    {
      name: "Placa",
      selector: ({ placa }: IDetalheLoteGerenciarPagamento) => placa ?? "-",
      width: "100px",
    },
    {
      name: "Renavam",
      selector: ({ renavam }: IDetalheLoteGerenciarPagamento) => renavam ?? "-",
      width: "120px",
    },
    {
      name: "Chassi",
      selector: ({ chassi }: IDetalheLoteGerenciarPagamento) => chassi ?? "-",
      width: "160px",
    },
    {
      name: "Empresa",
      selector: ({ nomeEmpresa }: IDetalheLoteGerenciarPagamento) =>
        nomeEmpresa ?? "-",
    },
    {
      name: "Serviço",
      selector: ({ servico }: IDetalheLoteGerenciarPagamento) => servico ?? "-",
    },
    {
      name: <span>Custos lançados</span>,
      selector: ({ custosLancados }: IDetalheLoteGerenciarPagamento) => (
        <CustosLancadosColunaCell custos={custosLancados ?? []} />
      ),
      width: "200px",
    },
    {
      name: "Custos",
      selector: ({ custos }: IDetalheLoteGerenciarPagamento) =>
        custos != null ? formatarDinheiro(Number(custos)) : "-",
      width: "110px",
    },
    {
      name: "A receber",
      selector: ({
        aReceber,
        alertaAReceber,
      }: IDetalheLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>
            {formatarDinheiro(Number(aReceber ?? 0))}
          </span>
          {alertaAReceber && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Valor do serviço a receber inferior ao custo do processo</Tooltip>}
            >
              <AlertaAReceber>
                <FaExclamationTriangle />
              </AlertaAReceber>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "120px",
    },
  ];

  return (
    <ModalCustom
      handleClose={handleClose}
      show={show}
      centered
      size="xl"
      title={`Lote de pagamento ${dados?.idLote ?? ""}`}
      footer
      footerTools={
        <ContainerFechar>
          <PrimaryButton onClick={handleClose} outline variante="primary">
            Fechar
          </PrimaryButton>
        </ContainerFechar>
      }
    >
      {dados && (
        <>
          <ContainerResumo>
            <LinhaInfo>
              <CamposGroup>
                <CampoResumo>
                  <LabelResumo>Tipo de lançamento</LabelResumo>
                  <ValorResumo>
                    {dados.tipoLancamento
                      ? (TIPO_LANCAMENTO_LABEL[dados.tipoLancamento] ??
                        dados.tipoLancamento)
                      : "-"}
                  </ValorResumo>
                </CampoResumo>
                <CampoResumo>
                  <LabelResumo>Beneficiário</LabelResumo>
                  <ValorResumo>{dados.nomeBeneficiario ?? "-"}</ValorResumo>
                </CampoResumo>
                <CampoResumo>
                  <LabelResumo>Nº Recibo / NF</LabelResumo>
                  <ValorResumo>{dados.numeroNfRecibo ?? "-"}</ValorResumo>
                </CampoResumo>
                <CampoResumo>
                  <LabelResumo>Valor total do Recibo / NF</LabelResumo>
                  <ValorResumo>
                    {dados.valorTotalNfRecibo != null
                      ? formatarDinheiro(Number(dados.valorTotalNfRecibo))
                      : "-"}
                  </ValorResumo>
                </CampoResumo>
                <CampoResumo>
                  <LabelResumo>Recibo / NF</LabelResumo>
                  {dados.temArquivoNfRecibo ? (
                    <LinkRecibo
                      href="#"
                      onClick={(e) => { e.preventDefault(); onBaixarRecibo(dados.idLote); }}
                    >
                      Visualizar <FaExternalLinkAlt style={{ fontSize: "0.75rem" }} />
                    </LinkRecibo>
                  ) : (
                    <ValorResumo>-</ValorResumo>
                  )}
                </CampoResumo>
              </CamposGroup>
            </LinhaInfo>
            <ObservacaoResumo>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <div>
                  <ObservacaoLabel>Observação</ObservacaoLabel>
                  <ObservacaoTexto>{dados.observacao ?? "-"}</ObservacaoTexto>
                </div>
                {dados.pagamentoUrgente && (
                  <UrgenteLinha>
                    <FlagUrgente>
                      <FaFlag />
                    </FlagUrgente>
                    Pagamento urgente
                  </UrgenteLinha>
                )}
              </div>
            </ObservacaoResumo>
          </ContainerResumo>

          <CustosAdicionados>
            <span>
              Custos adicionados:{" "}
              <strong>
                {dados.custosAdicionados != null
                  ? formatarDinheiro(Number(dados.custosAdicionados))
                  : "-"}
              </strong>
            </span>
            <BuscaWrapper>
              <BuscaIcone><FaSearch /></BuscaIcone>
              <BuscaInput
                placeholder="ID Processo ou placa"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </BuscaWrapper>
          </CustosAdicionados>
          <hr style={{ color: "#9ca3af" }}></hr>
          <ContainerTable>
            <Table
              keyField="id"
              pagination={false}
              columns={columns}
              data={processosFiltrados}
              loading={false}
              noSubHeader
              possuiTooltip={false}
              headerFixo
              fixedHeaderScrollHeight="350px"
            />
          </ContainerTable>
        </>
      )}
    </ModalCustom>
  );
}
